import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import { Box, Flex } from '@chakra-ui/react';
import { Tag, Text } from '@kula-ai/components';
import { fillIdPlaceholders } from '../../../helpers';
import { useChatSubmit } from '../../../hooks';
import type {
  DashboardBlockData,
  DashboardCard,
  DashboardSection,
} from '../../../types';
import { SuggestedActionChips } from '../SuggestedActionChips';
import { dashboardChartOptions } from './chartOptions';

// columnrange (the percentile band series) lives in highcharts-more.
highchartsMore(Highcharts);

interface DashboardBlockProps {
  data: DashboardBlockData;
}

const TONE_BG: Record<string, { bg: string; color: string }> = {
  success: { bg: 'success-50', color: 'success-700' },
  warning: { bg: 'warning-50', color: 'warning-700' },
  error: { bg: 'error-50', color: 'error-700' },
  primary: { bg: 'primary-50', color: 'primary-600' },
  neutral: { bg: 'secondary-100', color: 'secondary-600' },
};

const tone = (t?: string) => TONE_BG[t ?? ''] ?? TONE_BG.neutral;

const SectionHeader = ({ section }: { section: DashboardSection }) => {
  if (!section.title && !section.confidence && !section.source) return null;
  return (
    <Flex align="center" gap="8px" mb="6px">
      {section.title && (
        <Text variant="span" className="text-secondary-900 font-semibold">
          {section.title}
        </Text>
      )}
      {section.confidence && (
        <Flex
          px="6px"
          py="1px"
          borderRadius="full"
          bg={tone(section.confidence === 'low' ? 'warning' : 'neutral').bg}
        >
          <Text variant="small" className="text-secondary-600">
            {section.confidence} confidence
          </Text>
        </Flex>
      )}
      {section.source && (
        <Text variant="small" className="text-secondary-400">
          {section.source}
        </Text>
      )}
    </Flex>
  );
};

const KpiTiles = ({ section }: { section: DashboardSection }) => (
  <Flex wrap="wrap" gap="8px">
    {(section.kpis ?? []).map((k) => {
      const t = tone(k.tone);
      return (
        <Box
          key={k.label}
          flex="1 1 150px"
          minW="150px"
          border="1px solid"
          borderColor="secondary-200"
          borderRadius="10px"
          bg="base-white"
          px="12px"
          py="10px"
        >
          <Text
            variant="small"
            className="text-secondary-500"
            style={{ display: 'block' }}
          >
            {k.label}
          </Text>
          <Text
            variant="span"
            className="font-semibold"
            style={{ color: `var(--chakra-colors-${t.color})`, fontSize: 18 }}
          >
            {k.value}
          </Text>
          {k.hint && (
            <Text
              variant="small"
              className="text-secondary-400"
              style={{ display: 'block' }}
            >
              {k.hint}
            </Text>
          )}
        </Box>
      );
    })}
  </Flex>
);

const SplitBar = ({ section }: { section: DashboardSection }) => {
  const segs = section.split ?? [];
  const total = segs.reduce((acc, s) => acc + s.pct, 0) || 100;
  return (
    <Box>
      <Flex
        h="26px"
        borderRadius="8px"
        overflow="hidden"
        border="1px solid"
        borderColor="secondary-200"
      >
        {segs.map((s) => (
          <Flex
            key={s.label}
            align="center"
            justify="center"
            bg={tone(s.tone).bg}
            style={{ width: `${(s.pct / total) * 100}%` }}
            minW="40px"
          >
            <Text variant="small" className="font-medium text-secondary-700">
              {s.label} {Math.round(s.pct)}%
            </Text>
          </Flex>
        ))}
      </Flex>
      {section.text && (
        <Text
          variant="small"
          className="text-secondary-500"
          style={{ marginTop: 4, display: 'block' }}
        >
          {section.text}
        </Text>
      )}
    </Box>
  );
};

const CardGrid = ({ cards }: { cards: DashboardCard[] }) => (
  <Flex wrap="wrap" gap="8px">
    {cards.map((c) => {
      const accent = c.tone ? tone(c.tone) : null;
      return (
        <Box
          key={c.title}
          flex="1 1 230px"
          minW="230px"
          maxW="100%"
          border="1px solid"
          borderColor={accent ? `${c.tone}-300` : 'secondary-200'}
          borderRadius="10px"
          bg="base-white"
          px="12px"
          py="10px"
        >
          <Flex align="center" justify="space-between" gap="8px">
            <Text variant="span" className="text-secondary-900 font-semibold">
              {c.title}
            </Text>
            <Flex align="center" gap="4px" flexShrink={0}>
              {c.meter && (
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={
                    c.meter === 'high'
                      ? 'success-500'
                      : c.meter === 'medium'
                        ? 'warning-500'
                        : 'secondary-300'
                  }
                  title={`poachability: ${c.meter}`}
                />
              )}
              {c.badge && (
                <Flex
                  px="6px"
                  py="1px"
                  borderRadius="full"
                  bg={accent?.bg ?? 'secondary-100'}
                >
                  <Text
                    variant="small"
                    className="font-medium text-secondary-600"
                  >
                    {c.badge}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
          {c.body && (
            <Text
              variant="small"
              className="text-secondary-600"
              style={{ display: 'block', marginTop: 2 }}
            >
              {c.body}
            </Text>
          )}
          {c.detail && (
            <Text
              variant="small"
              className="text-secondary-400"
              style={{ display: 'block', marginTop: 2 }}
            >
              {c.detail}
            </Text>
          )}
          {c.tags && c.tags.length > 0 && (
            <Flex wrap="wrap" gap="4px" mt="6px">
              {c.tags.map((t) => (
                <Tag key={t} label={t} size="sm" variant="outline" />
              ))}
            </Flex>
          )}
        </Box>
      );
    })}
  </Flex>
);

const GridSection = ({ section }: { section: DashboardSection }) => {
  const g = section.grid;
  if (!g) return null;
  return (
    <Box
      border="1px solid"
      borderColor="secondary-200"
      borderRadius="10px"
      overflow="hidden"
    >
      <Flex bg="secondary-50" px="12px" py="8px" gap="12px">
        {g.columns.map((c, i) => (
          <Box key={c} flex="1" minW="0">
            <Text
              variant="small"
              className={`font-semibold ${i === g.accentCol ? 'text-primary-600' : 'text-secondary-600'}`}
            >
              {c}
            </Text>
          </Box>
        ))}
      </Flex>
      {g.rows.map((row) => (
        <Flex
          key={row.join('|')}
          px="12px"
          py="8px"
          gap="12px"
          borderTop="1px solid"
          borderColor="secondary-100"
        >
          {row.map((cell, i) => (
            <Box key={`${g.columns[i] ?? i}-${cell}`} flex="1" minW="0">
              <Text
                variant="small"
                className={
                  i === g.accentCol
                    ? 'text-primary-600 font-medium'
                    : 'text-secondary-700'
                }
              >
                {cell}
              </Text>
            </Box>
          ))}
        </Flex>
      ))}
    </Box>
  );
};

const Section = ({ section }: { section: DashboardSection }) => {
  let body: React.ReactNode = null;
  switch (section.kind) {
    case 'kpis':
      body = <KpiTiles section={section} />;
      break;
    case 'split_bar':
      body = <SplitBar section={section} />;
      break;
    case 'chart': {
      const options = section.chart
        ? dashboardChartOptions(section.chart)
        : null;
      body = options ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : null;
      break;
    }
    case 'cards':
      body = <CardGrid cards={section.cards ?? []} />;
      break;
    case 'bands':
      body = (
        <Flex direction="column" gap="10px">
          {(section.bands ?? []).map((b) => (
            <Box key={b.name}>
              <Text
                variant="small"
                className="text-secondary-500 font-semibold"
                style={{ display: 'block', marginBottom: 4 }}
              >
                {b.name}
              </Text>
              {b.note && (
                <Text
                  variant="small"
                  className="text-secondary-400"
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  {b.note}
                </Text>
              )}
              <CardGrid cards={b.cards} />
            </Box>
          ))}
        </Flex>
      );
      break;
    case 'tags':
      body = (
        <Flex direction="column" gap="8px">
          {(section.tagSets ?? []).map((ts) => (
            <Box key={ts.label}>
              <Text
                variant="small"
                className="text-secondary-500"
                style={{ display: 'block', marginBottom: 4 }}
              >
                {ts.label}
              </Text>
              <Flex wrap="wrap" gap="6px">
                {ts.tags.map((t) => (
                  <Tag key={t} label={t} size="md" variant="outline" />
                ))}
              </Flex>
            </Box>
          ))}
          {section.text && (
            <Text variant="small" className="text-secondary-600">
              {section.text}
            </Text>
          )}
        </Flex>
      );
      break;
    case 'grid':
      body = <GridSection section={section} />;
      break;
    case 'list':
      body = (
        <Flex direction="column" gap="4px">
          {(section.items ?? []).map((item, i) => (
            <Flex key={item} gap="8px" align="baseline">
              <Text variant="small" className="text-secondary-400">
                {i + 1}.
              </Text>
              <Text variant="small" className="text-secondary-700">
                {item}
              </Text>
            </Flex>
          ))}
        </Flex>
      );
      break;
    case 'callout':
      body = (
        <Box bg="primary-50" borderRadius="10px" px="12px" py="10px">
          <Text variant="small" className="text-secondary-700">
            {section.text}
          </Text>
        </Box>
      );
      break;
    default:
      body = null;
  }
  if (!body) return null;
  return (
    <Box>
      <SectionHeader section={section} />
      {body}
    </Box>
  );
};

// The generic dashboard renderer: a backend-authored page spec drawn
// section-by-section. SCOPE: only the market-map and salary-benchmark skills
// emit this envelope — everything else keeps its bespoke block.
export const DashboardBlock = ({ data }: DashboardBlockProps) => {
  const { send, isSending } = useChatSubmit();
  if (!data.sections?.length) return null;

  const sendFollowup = (template: string) => {
    if (isSending) return;
    void send(fillIdPlaceholders(template, []));
  };

  return (
    <Flex
      direction="column"
      gap="14px"
      w="100%"
      border="1px solid"
      borderColor="secondary-200"
      borderRadius="12px"
      bg="base-white"
      px="16px"
      py="14px"
      data-test-id={`dashboard-block-${data.dashboard}`}
    >
      <Box>
        <Text
          variant="span"
          className="text-secondary-900 font-semibold"
          style={{ fontSize: 16 }}
        >
          {data.title}
        </Text>
        {data.subtitle && (
          <Text
            variant="small"
            className="text-secondary-500"
            style={{ display: 'block' }}
          >
            {data.subtitle}
          </Text>
        )}
      </Box>
      {data.sections.map((section, i) => (
        <Section
          key={`${section.kind}-${section.title ?? i}`}
          section={section}
        />
      ))}
      {data.suggestedActions && data.suggestedActions.length > 0 && (
        <SuggestedActionChips
          actions={data.suggestedActions}
          onSelect={sendFollowup}
          disabled={isSending}
          testIdPrefix="dashboard-suggested-action"
        />
      )}
    </Flex>
  );
};
